/**
 * MUI Component Overrides
 *
 * Default styling for MUI components to ensure consistency.
 * These can be overridden at the component level using sx prop.
 *
 * Version History:
 * v2.92 - 2026-02-07 - Mode-aware overrides for dark mode support
 */

import { Components, Theme } from '@mui/material/styles';
import {
  primary, secondary, accent, background, border,
  darkBackground, darkBorder,
} from './colors';

// v2.92 - Accept mode parameter for dark-mode-aware overrides
export const getComponentOverrides = (mode: 'light' | 'dark'): Components<Omit<Theme, 'components'>> => {
  const isDark = mode === 'dark';

  return {
    // Button styling
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: isDark
              ? '0 2px 8px rgba(0,0,0,0.4)'
              : '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
        containedPrimary: {
          backgroundColor: primary.main,
          '&:hover': {
            backgroundColor: primary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: accent.main,
          '&:hover': {
            backgroundColor: accent.dark,
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },

    // Card styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: isDark
            ? '0 1px 3px rgba(0,0,0,0.3)'
            : '0 1px 3px rgba(0,0,0,0.12)',
          border: `1px solid ${isDark ? darkBorder.light : border.light}`,
          backgroundColor: isDark ? darkBackground.paper : undefined,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },

    // Paper styling
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundImage: 'none', // v2.92 - Remove MUI dark mode gradient overlay
        },
        elevation1: {
          boxShadow: isDark
            ? '0 1px 3px rgba(0,0,0,0.3)'
            : '0 1px 3px rgba(0,0,0,0.12)',
        },
        elevation2: {
          boxShadow: isDark
            ? '0 2px 6px rgba(0,0,0,0.4)'
            : '0 2px 6px rgba(0,0,0,0.15)',
        },
      },
    },

    // TextField styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: primary.main,
              borderWidth: 2,
            },
          },
        },
      },
    },

    // Chip styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: isDark ? `${primary.main}30` : `${primary.main}15`,
          color: isDark ? primary.light : primary.main,
        },
        colorSuccess: {
          backgroundColor: isDark ? '#1B3D1F' : '#E8F5E9',
          color: isDark ? '#66BB6A' : '#2E7D32',
        },
        colorError: {
          backgroundColor: isDark ? '#3D1B1B' : '#FFEBEE',
          color: isDark ? '#EF5350' : '#C62828',
        },
        colorWarning: {
          backgroundColor: isDark ? '#3D2E14' : '#FFF3E0',
          color: isDark ? '#FFA726' : '#E65100',
        },
      },
    },

    // IconButton styling
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: isDark ? `${primary.main}20` : `${primary.main}10`,
          },
        },
      },
    },

    // AppBar styling (for TopBar)
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: isDark
            ? '0 1px 3px rgba(0,0,0,0.3)'
            : '0 1px 3px rgba(0,0,0,0.1)',
          backgroundImage: 'none', // v2.92 - Remove MUI dark mode gradient overlay
        },
      },
    },

    // Drawer styling (for Sidebar)
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: isDark ? darkBackground.sidebar : secondary.main,
          color: '#FFFFFF',
          borderRight: 'none',
          backgroundImage: 'none', // v2.92 - Remove MUI dark mode gradient overlay
        },
      },
    },

    // ListItem styling (for navigation)
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,255,255,0.15)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
            },
          },
        },
      },
    },

    // Table styling
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: isDark ? darkBackground.card : background.default,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${isDark ? darkBorder.light : border.light}`,
        },
      },
    },

    // Dialog styling
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundImage: 'none', // v2.92 - Remove MUI dark mode gradient overlay
        },
      },
    },

    // Tooltip styling
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDark ? darkBackground.card : secondary.main,
          borderRadius: 6,
          fontSize: '0.75rem',
          padding: '6px 12px',
        },
      },
    },

    // Fab styling (for floating chat button)
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: isDark
            ? '0 4px 12px rgba(0,0,0,0.4)'
            : '0 4px 12px rgba(0,0,0,0.15)',
          '&:hover': {
            boxShadow: isDark
              ? '0 6px 16px rgba(0,0,0,0.5)'
              : '0 6px 16px rgba(0,0,0,0.2)',
          },
        },
        primary: {
          backgroundColor: primary.main,
          '&:hover': {
            backgroundColor: primary.dark,
          },
        },
      },
    },

    // Skeleton styling (for loading states)
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : undefined,
        },
      },
    },

    // Alert styling
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: isDark ? '#1B3D1F' : '#E8F5E9',
          color: isDark ? '#66BB6A' : '#1B5E20',
        },
        standardError: {
          backgroundColor: isDark ? '#3D1B1B' : '#FFEBEE',
          color: isDark ? '#EF5350' : '#C62828',
        },
        standardWarning: {
          backgroundColor: isDark ? '#3D2E14' : '#FFF3E0',
          color: isDark ? '#FFA726' : '#E65100',
        },
        standardInfo: {
          backgroundColor: isDark ? '#142D3D' : '#E3F2FD',
          color: isDark ? '#42A5F5' : '#0D47A1',
        },
      },
    },
  };
};

// v2.92 - Backward compatibility: light mode overrides
export const componentOverrides = getComponentOverrides('light');

export default componentOverrides;
